// Explicit client state machine for Dice. Every flow transition is validated
// against an allowlist so illegal transitions (e.g. starting a second roll
// mid-animation) throw instead of silently corrupting state. The machine
// carries NO game data; it only governs WHICH operations are permitted in each
// phase. Rolling is locked unless the machine is READY, which is the single
// guard against double-submits.

export type GameState =
  | 'BOOTING'
  | 'LOADING'
  | 'EXCHANGING_TOKEN'
  | 'READY'
  | 'ROLL_REQUESTED'
  | 'WAITING_FOR_RESULT'
  | 'ANIMATING'
  | 'SHOWING_WIN'
  | 'RECOVERING'
  | 'SESSION_EXPIRED'
  | 'ERROR';

const TRANSITIONS: Record<GameState, readonly GameState[]> = {
  BOOTING: ['LOADING', 'ERROR'],
  LOADING: ['EXCHANGING_TOKEN', 'ERROR'],
  EXCHANGING_TOKEN: ['RECOVERING', 'READY', 'SESSION_EXPIRED', 'ERROR'],
  RECOVERING: ['READY', 'ANIMATING', 'SHOWING_WIN', 'SESSION_EXPIRED', 'ERROR'],
  READY: ['ROLL_REQUESTED', 'RECOVERING', 'SESSION_EXPIRED', 'ERROR'],
  ROLL_REQUESTED: ['WAITING_FOR_RESULT', 'READY', 'SESSION_EXPIRED', 'ERROR'],
  WAITING_FOR_RESULT: ['ANIMATING', 'READY', 'SESSION_EXPIRED', 'ERROR'],
  ANIMATING: ['SHOWING_WIN', 'READY', 'SESSION_EXPIRED', 'ERROR'],
  SHOWING_WIN: ['READY', 'SESSION_EXPIRED', 'ERROR'],
  SESSION_EXPIRED: ['EXCHANGING_TOKEN'],
  ERROR: ['READY', 'EXCHANGING_TOKEN', 'RECOVERING'],
};

export type StateListener = (next: GameState, prev: GameState) => void;

export class StateMachine {
  private state: GameState;
  private readonly listeners = new Set<StateListener>();

  constructor(initial: GameState = 'BOOTING') {
    this.state = initial;
  }

  current(): GameState {
    return this.state;
  }

  /** True if a round is in a phase where the player must not start a new roll. */
  isBusy(): boolean {
    return this.state !== 'READY';
  }

  canTransition(to: GameState): boolean {
    return TRANSITIONS[this.state].includes(to);
  }

  /** Transitions to `to`, throwing if the transition is not permitted. */
  transition(to: GameState): void {
    if (!this.canTransition(to)) {
      throw new Error(`illegal transition ${this.state} -> ${to}`);
    }
    const prev = this.state;
    this.state = to;
    for (const l of this.listeners) l(to, prev);
  }

  subscribe(l: StateListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
}
